import { Injectable } from '@angular/core';
import { IUser } from '@epicuro-next/data/users';
import { List } from '@epicuro-next/models/engagement';
import { APP_PRIVATE_PATHS } from '@epicuro-next/navigation/constants';
import {
  IListClosedSessionEvent,
  IListOpenedSessionEvent,
  IListSessionEventPayload,
  IProductListOpenedEvent,
  ProductListAnalyticsEvent,
} from '@epicuro-next/platform/event-tracker';
import { selectActiveAccountId } from '@epicuro-next/state/accounts-state';
import { trackEvent } from '@epicuro-next/state/event-tracker';
import { selectListEntity } from '@epicuro-next/state/list-entities';
import { selectUser } from '@epicuro-next/state/user-state';
import { extractGuidFromString, isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { RouterNavigationAction, ROUTER_REQUEST } from '@ngrx/router-store';
import { select, Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { combineLatest, EMPTY, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  shareReplay,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import { lookClosed, trackLookBook } from './shop-the-look-core.actions';
import {
  selectLookBookInRoute,
  selectLookBookListInRoute,
} from './shop-the-look-core.selectors';

@Injectable()
export class ShopTheLookTrackingEffects {
  private user$ = this.store.pipe(
    select(selectUser),
    filter(isNonNullish),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private accountId$ = this.store.pipe(
    select(selectActiveAccountId),
    filter(isNonNullish),
  );

  private lookBookListInRoute$ = this.store.pipe(
    select(selectLookBookListInRoute),
    filter(isNonNullish),
  );

  constructor(private actions$: Actions, private store: Store) {}

  public trackLookOpened$ = createEffect(() =>
    combineLatest([
      this.store.pipe(select(selectLookBookInRoute), filter(isNonNullish)),
      this.store.pipe(select(selectLookBookListInRoute), filter(isNonNullish)),
    ]).pipe(
      filter(([lookBook, list]) =>
        lookBook.items.some((item) => item.productListId === list.id),
      ),
      distinctUntilChanged(
        ([lookBookA, listA], [lookBookB, listB]) =>
          lookBookA.id === lookBookB.id && listA.id === listB.id,
      ),
      withLatestFrom(this.user$),
      map(([[lookBook, list], user]) => {
        const event: IListOpenedSessionEvent = {
          name: ProductListAnalyticsEvent.ListOpenedSessionEvent,
          properties: userLookPairToAnalyticsSessionEventPayload(
            list,
            lookBook.id,
            user,
          ),
        };
        return trackEvent({ event });
      }),
    ),
  );

  public dispatchLookClosed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_REQUEST),
      filter(
        ({ payload: { routerState, event } }: RouterNavigationAction) =>
          isNonNullish(routerState.url) &&
          routerState.url.includes(APP_PRIVATE_PATHS.shopTheLook.path) &&
          routerState.url !== event.url,
      ),
      map((action) => action.payload.routerState.url),
      mergeMap((url) => {
        const [, urlAfterShopTheLookPath] = url.split(
          APP_PRIVATE_PATHS.shopTheLook.path,
        );

        if (!urlAfterShopTheLookPath) return EMPTY;

        // [,lookBookId, listId] because input is '/<id>/<some-other-route>'
        const [, lookBookId, listId] = urlAfterShopTheLookPath.split('/');
        return of(lookClosed({ lookBookId, listId }));
      }),
    ),
  );

  public trackLookClosedSessionEvent$ = createEffect(() =>
    combineLatest([this.actions$.pipe(ofType(lookClosed)), this.user$]).pipe(
      filter(isNonNullish), // without this combineLatest dispatches empty object which is weird
      mergeMap(([lookClosedAction, userData]) =>
        this.listIdAndUserToListEntityStream(lookClosedAction, userData).pipe(
          map(({ list, user }) => {
            const event: IListClosedSessionEvent = {
              name: ProductListAnalyticsEvent.ListClosedSessionEvent,
              properties: userLookPairToAnalyticsSessionEventPayload(
                list,
                lookClosedAction.lookBookId,
                user,
              ),
            };
            return trackEvent({ event });
          }),
        ),
      ),
    ),
  );

  public trackShopTheLookFromUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_REQUEST),
      filter(
        ({ payload: { event, routerState } }: RouterNavigationAction) =>
          event.url.includes(APP_PRIVATE_PATHS.shopTheLook.path) &&
          routerState.url.length === 0,
      ),
      map(({ payload: { event } }) => extractGuidFromString(event.url)),
      filter(([, productListId]) => isNonNullish(productListId)),
      distinctUntilChanged(),
      map(([lookBookId, productListId]) =>
        trackLookBook({
          lookbookId: lookBookId,
          productListId,
          source: 'url',
        }),
      ),
    ),
  );

  public trackLookbook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(trackLookBook),
      switchMap(({ lookbookId, productListId, source }) =>
        combineLatest([
          this.user$,
          this.accountId$,
          this.lookBookListInRoute$,
        ]).pipe(
          take(1),
          map(([user, accountId, list]) => {
            const event: IProductListOpenedEvent = {
              name: ProductListAnalyticsEvent.ProductListOpened,
              properties: {
                userId: user.userId,
                externalUserId: user?.externalId,
                accountId,
                ownerId: list.creation.user?.externalId,
                externalOwnerId: list.creation.user?.externalId,
                source,
                productListId,
                lookbookId,
              },
            };
            return trackEvent({
              event,
            });
          }),
        ),
      ),
    ),
  );

  private listIdAndUserToListEntityStream(
    action: { listId: string; lookBookId: string },
    user: IUser | null,
  ) {
    return this.store.pipe(
      select(selectListEntity, action.listId),
      map((entity) => ({
        list: entity ? entity.list : undefined,
        lookBookId: action.lookBookId,
        user,
      })),
      filter(
        (
          payload,
        ): payload is {
          list: List;
          lookBookId: string;
          user: IUser;
        } => isNonNullish(payload.list),
      ),
      take(1),
    );
  }
}

function userLookPairToAnalyticsSessionEventPayload(
  list: List,
  lookBookId: string,
  user: IUser,
): IListSessionEventPayload {
  return {
    productListId: list.id,
    lookBookId,
    ownerExternalId: list.creation.user?.externalId,
    ownerInternalId: list.creation.user?.id,
    ownerId: list.creation.user?.externalId ?? undefined,
    externalUserId: user.externalId,
    internalUserId: user.id,
    eventOccurredDateTime: DateTime.utc().toISO(),
  };
}
